/**
 * MODULE ÉTUDIANTS — les règles d'AFFICHAGE, en un seul endroit
 * ============================================================
 * Lot 1.3. Module pur : aucune JSX, aucun appel réseau, aucune dépendance React.
 * Testable au banc comme `apogeeImport.js` l'est pour l'appariement.
 *
 * Pourquoi un module à part plutôt que des fonctions dans chaque composant : la
 * liste et la fiche affichent LES MÊMES notions — un statut, un régime, un boursier,
 * un avatar, une date. Deux copies, et le badge « Abandon » finit vert d'un côté et
 * gris de l'autre. C'est la règle du projet : réutiliser le vocabulaire visuel plutôt
 * que d'en inventer un par écran.
 *
 * Les fonctions rendent `{ classe, libelle }` et jamais du JSX : le composant décide
 * de la balise, ce module décide du vocabulaire.
 */

// ═══════════════════════════════════════════════════════════════════════════════
// L'AVATAR
// ═══════════════════════════════════════════════════════════════════════════════

/** Initiales : première lettre du nom, première du prénom. Deux lettres au plus. */
export function initiales(nom, prenom) {
    const a = String(nom || '').trim().charAt(0).toUpperCase();
    const b = String(prenom || '').trim().charAt(0).toUpperCase();
    return (a + b) || '?';
}

/**
 * Teinte de l'avatar, DÉRIVÉE DES INITIALES — écart **E3**, décidé au lot 1.0.
 *
 * La maquette colore l'avatar selon le sexe (bleu pour les garçons, rose sinon) et se
 * contredit elle-même : `BARRY Aicha` y porte la classe `male`. Colorier une personne
 * par son sexe est une inférence visible que rien dans l'application ne réclame — et
 * la première contradiction serait lue comme une erreur de données.
 *
 * La teinte est donc une fonction du texte, stable et sans signification : elle sert
 * seulement à distinguer deux lignes voisines d'un coup d'œil.
 */
export function teinteAvatar(nom, prenom) {
    const texte = String(nom || '') + String(prenom || '');
    let somme = 0;
    for (let i = 0; i < texte.length; i++) somme = (somme * 31 + texte.charCodeAt(i)) % 360;
    // Saturation et luminosité fixes : c'est ce qui garantit que le blanc du texte
    // reste lisible sur les 360 teintes. Une teinte libre et une luminosité libre
    // auraient produit du jaune pâle sur blanc.
    return `hsl(${somme}, 52%, 46%)`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// LES BADGES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Statut. Les quatre valeurs de l'écart **E30** — « redoublant » n'est plus un statut,
 * il est devenu un badge à part.
 *
 * Le repli n'est pas décoratif : le CHECK de la base autorise encore `redoublant`, et
 * une valeur héritée doit s'AFFICHER telle quelle plutôt que de disparaître. Un statut
 * qu'on ne sait pas nommer se montre en gris, avec sa valeur brute — jamais rien.
 */
const STATUTS_AFFICHAGE = {
    actif:      { classe: 'etu-badge-actif',     libelle: 'Actif' },
    abandon:    { classe: 'etu-badge-abandon',   libelle: 'Abandon' },
    mobilite:   { classe: 'etu-badge-mobilite',  libelle: 'Mobilité internationale' },
    suspendu:   { classe: 'etu-badge-suspendu',  libelle: 'Suspendu' },
    redoublant: { classe: 'etu-badge-redoublant', libelle: 'Redoublant (statut hérité)' }
};

export function badgeStatut(statut) {
    const cle = String(statut || '').trim();
    if (!cle) return { classe: 'etu-badge-inconnu', libelle: '—' };
    return STATUTS_AFFICHAGE[cle] || { classe: 'etu-badge-inconnu', libelle: cle };
}

/** Régime. Les trois classes existent depuis le lot 1.1 (carte de mapping). */
const REGIMES_AFFICHAGE = {
    initiale:  { classe: 'etu-regime-initiale',  libelle: 'Initiale' },
    apprentis: { classe: 'etu-regime-apprentis', libelle: 'Apprentis' },
    fc:        { classe: 'etu-regime-fc',        libelle: 'FC' }
};

/**
 * Libellés COURTS, ceux de la barre de filtres de la maquette (« Initiale »,
 * « Apprentis », « FC ») : dans une cellule de tableau, « Formation continue » double
 * la largeur de la colonne. Les libellés longs du serveur restent ceux des menus
 * déroulants, où la place existe.
 */
export function badgeRegime(regime) {
    const cle = String(regime || '').trim();
    if (!cle) return { classe: 'etu-regime-inconnu', libelle: '—' };
    return REGIMES_AFFICHAGE[cle] || { classe: 'etu-regime-inconnu', libelle: cle };
}

// ═══════════════════════════════════════════════════════════════════════════════
// LES LIBELLÉS DE LA FICHE — lot 1.3 bis
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Le libellé de chaque ligne de la fiche, en un seul endroit.
 *
 * Il n'y vit pas par goût du rangement : après un enregistrement, le bandeau doit dire
 * « Nom, Portable et Mention du bac enregistrés » à partir des CLÉS que le serveur
 * renvoie. Les libellés écrits en dur dans la JSX auraient obligé à en tenir une
 * seconde liste ici — et « Date naiss. » d'un côté serait devenu « Naissance » de
 * l'autre au premier remaniement.
 *
 * Les clés qui ne sont pas des champs modifiables (INE, formation, régime, e-mail
 * institutionnel) y figurent aussi : ce sont des lignes de la fiche, elles ont un
 * libellé, et rien ne leur pose de cadenas puisqu'elles ne sont pas verrouillables.
 */
export const LIBELLES_CHAMPS = {
    // Statut étudiant
    statut: 'Statut',
    boursier: 'Boursier',
    redoublant: 'Redoublant',
    // Identité
    ine: 'INE',
    nom: 'Nom',
    prenom: 'Prénom',
    sexe: 'Sexe',
    date_naissance: 'Date naiss.',
    // Bac & cursus
    type_bac: 'Série',
    mention_bac: 'Mention',
    annee_bac: 'Année',
    cursus: 'Cursus',
    // Contact
    email: 'Email perso.',
    email_institutionnel: 'Email instit.',
    telephone: 'Portable',
    // Suivi pédagogique
    formation: 'Formation',
    regime: 'Régime',
    // 2026-10-22 : les deux champs modifiables de l'inscription (le bandeau d'enregistrement les nomme par leur clé).
    sous_niveau_id: 'Formation',
    regime_inscription: 'Régime',
    parcours_pn_id: 'Parcours',
    // Deux classements, et il faut que les libellés les distinguent À L'ŒIL : APOGEE
    // donne le rang dans la promotion inscrite, SCODOC le rang à l'admission sur tous
    // les candidats classés. Mesuré : sur les 48 lignes qui portent les deux, 47
    // diffèrent (§0.5). Deux fois « Classement » dans la même fiche laisserait croire
    // à un doublon d'affichage.
    classement_admission: 'Classement',
    classement_recrutement: 'Rang admission',
    // Le TROISIÈME rang de la fiche, et le plus court à nommer : « Rang promo » se
    // distingue de « Classement » (APOGEE, promotion inscrite) et de « Rang admission »
    // (SCODOC, candidats classés). Le semestre s'affiche à côté, pas dans le libellé.
    rang_promotion: 'Rang promo',
    enseignant_referent_id: 'Référent'
};

/** Libellé d'une clé, ou la clé elle-même : un bilan ne doit jamais rendre une case vide. */
export function libelleChamp(cle) {
    return LIBELLES_CHAMPS[cle] || cle;
}

/** « Nom, Portable et Mention du bac » — l'énumération française, avec son « et ». */
export function enumererChamps(cles) {
    const mots = (cles || []).map(libelleChamp);
    if (mots.length <= 1) return mots.join('');
    return mots.slice(0, -1).join(', ') + ' et ' + mots[mots.length - 1];
}

// ═══════════════════════════════════════════════════════════════════════════════
// LES VALEURS
// ═══════════════════════════════════════════════════════════════════════════════

/** Date affichable, `jj/mm/aaaa` : la fonction commune (lot « Dates »), réexportée ici pour les écrans Étudiants. */
export { dateFr } from './dates';

/** Booléen de base : `null` n'est PAS `false` — « inconnu » se dit, il ne se devine pas. */
export function ouiNon(valeur) {
    if (valeur === true) return 'Oui';
    if (valeur === false) return 'Non';
    return '—';
}

/**
 * UN RANG SUR SON DÉNOMINATEUR — « 34 / 210 » (lot 1.4).
 *
 * Le dénominateur peut manquer alors que le rang existe : il n'est écrit que sur les
 * lignes qui portent un rang, et une reprise de données peut laisser l'un sans l'autre.
 * On affiche alors le rang SEUL — « 34 / null » se lirait comme un défaut, et
 * « 34 / 0 » comme un classement impossible.
 */
export function rangSurMax(rang, max) {
    if (rang === null || rang === undefined || rang === '') return null;
    return max ? `${rang}/${max}` : String(rang);
}

/** Valeur vide : un tiret cadratin, jamais une case blanche qui ressemble à un défaut. */
export function ouTiret(valeur) {
    const t = valeur === null || valeur === undefined ? '' : String(valeur).trim();
    return t || '—';
}
